/**
 * Progress — Base UI Progress (linear bar).
 *
 * Modes:
 *   • Determinate: pass `value` 0–100 (or null for indeterminate).
 *
 * Design system mapping:
 *   • Track:     bg-webex-gray-100 dark:bg-webex-gray-900 h-1.5 rounded-full
 *   • Indicator: bg-webex-blue-600
 *   • Label:     text-xs text-webex-gray-700 dark: webex-gray-300
 */
import * as React from "react"
import { Progress as BaseProgress } from "@base-ui-components/react/progress"
import { cn } from "@lib/cn"

export interface ProgressProps
  extends React.ComponentPropsWithoutRef<typeof BaseProgress.Root> {
  label?: React.ReactNode
  showValue?: boolean
}

export function Progress({ className, label, showValue, value, ...props }: ProgressProps) {
  return (
    <BaseProgress.Root value={value} className={cn("flex flex-col gap-1.5", className)} {...props}>
      {(label || showValue) && (
        <div className="flex items-center justify-between text-xs text-webex-gray-700 dark:text-webex-gray-300">
          {label && <BaseProgress.Label>{label}</BaseProgress.Label>}
          {showValue && (
            <BaseProgress.Value>
              {(formatted: string | null) => formatted ?? "…"}
            </BaseProgress.Value>
          )}
        </div>
      )}
      <BaseProgress.Track className="relative h-1.5 w-full overflow-hidden rounded-full bg-webex-gray-100 dark:bg-webex-gray-900">
        <BaseProgress.Indicator
          className={cn(
            "h-full rounded-full bg-webex-blue-600 transition-[width] duration-200 motion-reduce:transition-none",
            "data-[indeterminate]:w-1/3 data-[indeterminate]:animate-[progress-slide_1.5s_ease-in-out_infinite] motion-reduce:data-[indeterminate]:animate-none",
          )}
        />
      </BaseProgress.Track>
      <style>{`
        @keyframes progress-slide {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(300%); }
        }
      `}</style>
    </BaseProgress.Root>
  )
}
